import {AfterViewInit, Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild} from '@angular/core';

import {AirportSearchResult} from '../../airport/models/airport-search-result.model';
import {BusinessFuel, FuelType} from '../../business/models/business-fuel.model';
import {AirportFuelSummary} from '../../airport/models/airport-fuel-summary.model';
import {Router} from '@angular/router';
import {Subscription} from 'rxjs';
import {GlobalSettingsService} from '../services/global-settings.service';
import { Source } from '../models/source.model';


@Component({
  selector: 'app-airport-summary-card',
  templateUrl: './airport-summary-card.component.html',
  styleUrls: ['./airport-summary-card.component.scss']
})
export class AirportSummaryCardComponent implements AfterViewInit, OnInit, OnDestroy {
  @Input() airport: AirportSearchResult;
  @Input() fuelType: FuelType = FuelType._100LL;
  @Input() active: boolean;
  @Input() compact = false;
  @Input() interactive = false;
  @Output() airportMouseEnter = new EventEmitter<AirportSearchResult>();
  @Output() airportMouseLeave = new EventEmitter<AirportSearchResult>();

  @ViewChild('link') linkEl: ElementRef;

  private _touchStartX: number = null;
  private _touchStartY: number = null;

  fuelTypeSubscription: Subscription;

  get identifier(): string {
    if (this.airport.identifier != null && this.airport.identifier.toUpperCase().startsWith('ZZ')) {
      return '';
    } else {
      return '(' + this.airport.identifier + ')';
    }
  }

  get minFuel(): number {
    if ((this.airport.fuel || []).length < 1) {
      return null;
    }

    const minFuel = this.airport.fuel
      .filter(f => {
        if (this.fuelType === FuelType.JetA && (f.fuelType === FuelType.JetA || f.fuelType === FuelType.JetAPremix)) {
          return true;
        }
        return f.fuelType === this.fuelType;
      })
      .sort((f1: AirportFuelSummary, f2: AirportFuelSummary) => f1.price - f2.price)[0];

    return minFuel ? minFuel.price : null;
  }

  get fuelTypeStr(): string {
    return BusinessFuel.formatFuelType(this.fuelType);
  }

  constructor(private _router: Router,
              private _globalSettingsService: GlobalSettingsService) { }

  ngOnInit() {
    this.fuelTypeSubscription = this._globalSettingsService.fuelType.subscribe(fuel => {
      this.fuelType = BusinessFuel.parseFuelType(fuel);
    });
  }

  ngOnDestroy() {
    this.fuelTypeSubscription.unsubscribe();
  }

  ngAfterViewInit(): void {
    // Workaround to avoid having to double tap on the card to navigate to the airport detail page, without breaking
    // scrolling.
    this.linkEl.nativeElement.addEventListener('touchstart', e => this.onTouchStart(e), { passive: true });
    this.linkEl.nativeElement.addEventListener('touchend', e => this.onTouchEnd(e), { passive: true });
  }

  onHover(hover: boolean) {
    if (hover) {
      this.airportMouseEnter.emit(this.airport);
    } else {
      this.airportMouseLeave.emit(this.airport);
    }
  }

  onTouchStart(e: TouchEvent) {
    const touch = e.changedTouches[0];
    if (touch) {
      this._touchStartX = touch.pageX;
      this._touchStartY = touch.pageY;
    }
  }

  onTouchEnd(e: TouchEvent) {
    // If the page coordinates of the touchend event are the same as touchstart, then navigate to the airport detail
    // page.
    if (this.isTap(e)) {
      if (this.airport.sourceId === Source.CaribbeanGuide || this.airport.sourceId === Source.BahamasGuide) {
        this._router.navigate(['/airports',  this.airport.identifier, 'island']);
      } else {
        this._router.navigate(['/airports', this.airport.identifier]);
      }
    }
  }

  /**
   * Returns true if the page coordinates of the touch end event are the same as for touch start, indicating that the
   * action is a tap instead of scroll. (Note that binding to the "tap" event does not work - without setting passive
   * to true, it breaks scrolling, and without setting passive to true, the event never fires.)
   * @param {TouchEvent} e
   * @return {boolean}
   */
  isTap(e: TouchEvent) {
    const touch = e.changedTouches[0];
    return touch ? this._touchStartX === touch.pageX && this._touchStartY === touch.pageY : false;
  }
}
